/*
 * Shared Fast Simon SDK loader, used by the rerank and widget integrations.
 *
 * Loads https://assets.fastsimon.com/sdk/latest/fast_simon_sdk.js once per page,
 * initialises it with the store's UUID + store_id, and exposes:
 *
 *   window.fastSimonSdk.ready()       → Promise<void> resolving when the SDK
 *                                       has finished initialising.
 *   window.fastSimonSdk.emit(name, d) → Fire an analytics event by short name
 *                                       (e.g. 'RecommendationProductClicked').
 *                                       Resolves to the event ID or null if
 *                                       FS isn't loaded yet.
 *
 * Initialisation is deduped via window-level promises so multiple consumers
 * on the same page share one SDK load.
 *
 * Reference: https://docs.fastsimon.com/sdk/docs/introduction-and-installation
 */
(function () {
  var SDK_URL = 'https://assets.fastsimon.com/sdk/latest/fast_simon_sdk.js';

  function loadSDK() {
    if (window.__fastSimonSdkLoad) return window.__fastSimonSdkLoad;
    window.__fastSimonSdkLoad = new Promise(function (resolve, reject) {
      if (window.FastSimonSDK) { resolve(); return; }
      // A template may already load the SDK from the head (the fs-sdk
      // collection template does, to start the download earlier). Wait on
      // that tag rather than adding a second copy.
      var existing = document.querySelector('script[src^="https://assets.fastsimon.com/sdk/"]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function (e) { reject(e); });
        return;
      }
      var s = document.createElement('script');
      s.setAttribute('src', SDK_URL);
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function (e) { reject(e); };
      document.head.appendChild(s);
    });
    return window.__fastSimonSdkLoad;
  }

  function initSDK() {
    if (window.__fastSimonSdkReady) return window.__fastSimonSdkReady;
    window.__fastSimonSdkReady = new Promise(function (resolve, reject) {
      if (!window.FAST_SIMON_UUID || !window.FAST_SIMON_STORE_ID) {
        reject(new Error('FAST_SIMON_UUID / FAST_SIMON_STORE_ID not set'));
        return;
      }
      try {
        window.FastSimonSDK.initialization({
          storeID: window.FAST_SIMON_STORE_ID,
          uuid: window.FAST_SIMON_UUID,
          type: 'MPA',
          onReady: function () { resolve(); },
        });
      } catch (e) {
        reject(e);
      }
    });
    return window.__fastSimonSdkReady;
  }

  function ready() {
    return loadSDK().then(initSDK);
  }

  // Fire an FS analytics event. `name` is a key in window.FastSimonEventName
  // (e.g. 'CollectionProductClicked') — looked up at fire time so the SDK has
  // a chance to load. Wrapped in try/catch and a no-op fallback so analytics
  // failures never break the page.
  function emit(name, data) {
    return ready().then(function () {
      if (!window.FastSimonSDK || !window.FastSimonEventName) return null;
      var resolved = window.FastSimonEventName[name];
      if (!resolved) return null;
      try {
        return window.FastSimonSDK.event({ eventName: resolved, data: data });
      } catch (e) { return null; }
    }).catch(function () { return null; });
  }

  window.fastSimonSdk = window.fastSimonSdk || { ready: ready, emit: emit };
})();
